import type { MentionUser } from '../api/commentsApi';

type TagSpan = { start: number; end: number; displayName: string };

// RegExp constructor, not a literal: a `/u`-flagged `\p{}` literal is TS1501
// under this project's `target: es5` (see src/i18n-jira-integrity.test.ts:20).
const WORD_CHAR = new RegExp('[\\p{L}\\p{N}]', 'u');

/**
 * Whether `@displayName` starts at `index` as a whole tag — not merely the prefix
 * of a longer name, which would attribute the mention to the wrong colleague.
 */
const matchesTagAt = (text: string, index: number, displayName: string): boolean => {
  // startsWith, never a regex built from a display name (Jira-supplied data).
  if (!text.startsWith(`@${displayName}`, index)) return false;
  const next = text[index + displayName.length + 1];
  return next === undefined || !WORD_CHAR.test(next);
};

/**
 * Locate every `@Display Name` tag of a known colleague, left to right.
 * Longest name first so "@Jean Dupont" is never read as "@Jean Dup" + "ont".
 */
const scanTags = (text: string, knownUsers: MentionUser[]): TagSpan[] => {
  const candidates = [...knownUsers].sort((a, b) => b.displayName.length - a.displayName.length);
  const spans: TagSpan[] = [];
  let i = 0;

  while (i < text.length) {
    // Plain for loop (not `.find`) so no function literal closes over the
    // loop-scoped `i` — same "first match in longest-first order wins" result.
    let hit: MentionUser | undefined;
    if (text[i] === '@') {
      for (const candidate of candidates) {
        if (matchesTagAt(text, i, candidate.displayName)) {
          hit = candidate;
          break;
        }
      }
    }
    if (hit) {
      const end = i + hit.displayName.length + 1;
      spans.push({ start: i, end, displayName: hit.displayName });
      i = end;
      continue;
    }
    i += 1;
  }

  return spans;
};

/**
 * Replace the in-progress `@token` with the full `@Display Name` tag, adding a
 * trailing space only when the following character isn't already whitespace
 * (otherwise inserting mid-sentence, e.g. "hi @jul ok", would double it up).
 * @param text - Current textarea value.
 * @param anchor - Span of the `@token` being replaced.
 * @param displayName - Selected colleague's display name.
 * @returns The new text and the caret position just after the inserted tag.
 */
export const insertMention = (
  text: string,
  anchor: { start: number; end: number },
  displayName: string,
): { text: string; caret: number } => {
  const rest = text.slice(anchor.end);
  const needsSpace = !/^\s/.test(rest);
  const tag = `@${displayName}${needsSpace ? ' ' : ''}`;
  return {
    text: text.slice(0, anchor.start) + tag + rest,
    caret: anchor.start + tag.length,
  };
};

/**
 * Colleagues whose tag is still present in the text — the mentions actually posted.
 * @param text - Current textarea value.
 * @param knownUsers - Colleagues picked so far, in selection order.
 * @returns The live subset, in selection order.
 */
export const deriveLiveMentions = (text: string, knownUsers: MentionUser[]): MentionUser[] => {
  const live = new Set(scanTags(text, knownUsers).map((span) => span.displayName));
  return knownUsers.filter((user) => live.has(user.displayName));
};

/**
 * Drop every tag bearing this display name, plus one adjacent space.
 * @param text - Current textarea value.
 * @param displayName - Display name to remove.
 * @param knownUsers - Colleagues picked so far (needed to resolve prefix collisions).
 * @returns The text without those tags.
 */
export const removeMentionsByName = (text: string, displayName: string, knownUsers: MentionUser[]): string => {
  const spans = scanTags(text, knownUsers).filter((span) => span.displayName === displayName);
  let out = text;

  // Right to left so each removal leaves the earlier spans' offsets valid.
  for (const span of [...spans].reverse()) {
    const hasTrailingSpace = out[span.end] === ' ';
    const end = hasTrailingSpace ? span.end + 1 : span.end;
    const start = !hasTrailingSpace && out[span.start - 1] === ' ' ? span.start - 1 : span.start;
    out = out.slice(0, start) + out.slice(end);
  }

  return out;
};
